import { useCallback, useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';

import HttpClient from '../../shared/infrastructures/clients/http.client';
import { extractApiErrorMessage } from '../../shared/utils/extract-api-error-message.util';
import { GetQuotesResponse, Quote } from '../types/quote.type';
import { QuoteStatus } from '../types/quote-status.type';
import { QUOTES_API_PATHS } from '../constants/quotes-paths.constants';

type UseAdminGetQuotesParams = {
  organizationId?: string;
  status?: QuoteStatus;
};

type UseAdminGetQuotesResult = {
  isLoading: boolean;
  quotes: Quote[];
  totalCount: number;
  refetch: () => Promise<void>;
};

const INITIAL_STATE: Omit<UseAdminGetQuotesResult, 'refetch'> = {
  isLoading: true,
  quotes: [],
  totalCount: 0,
};

export function useAdminGetQuotes(params: UseAdminGetQuotesParams): UseAdminGetQuotesResult {
  const { enqueueSnackbar } = useSnackbar();
  const [state, setState] = useState(INITIAL_STATE);

  const { organizationId, status } = params;

  const fetchQuotes = useCallback(async (): Promise<void> => {
    setState((prev) => ({ ...prev, isLoading: true }));

    try {
      const queryParams = new URLSearchParams();
      if (organizationId) queryParams.set('filters[organizationId]', organizationId);
      if (status) queryParams.set('filters[status]', status);

      const queryString = queryParams.toString();
      const url = queryString ? `${QUOTES_API_PATHS.LIST}?${queryString}` : QUOTES_API_PATHS.LIST;
      const response = await HttpClient.get<GetQuotesResponse>(url);

      setState({
        isLoading: false,
        quotes: response?.quotes ?? [],
        totalCount: response?.totalCount ?? 0,
      });
    } catch (error) {
      enqueueSnackbar(
        extractApiErrorMessage(error, 'Une erreur est survenue lors de la récupération des devis'),
        { variant: 'error' }
      );
      setState({ isLoading: false, quotes: [], totalCount: 0 });
    }
  }, [organizationId, status, enqueueSnackbar]);

  useEffect(() => {
    fetchQuotes();
  }, [fetchQuotes]);

  return { ...state, refetch: fetchQuotes };
}
